// The archived chat arrives as an array, a JSON string, or a double-encoded one — healthcare's `json` cast re-encodes what talk already stringified.

import { ConsultChatAttachment, ConsultChatMessage } from "../../types/appointments/ConsultChatTypes";

const SENDER_ROLES: string[] = ["practitioner", "patient"];
const MESSAGE_TYPES: string[] = ["text", "file"];

const toAttachment = (value: unknown): ConsultChatAttachment | null => {
	if (!value || typeof value !== "object") return null;

	const raw = value as Record<string, unknown>;
	const name = typeof raw.name === "string" ? raw.name.trim() : "";
	if (!name) return null;

	return {
		id: typeof raw.id === "string" ? raw.id : "",
		name,
		mime: typeof raw.mime === "string" ? raw.mime : "",
		size: typeof raw.size === "number" && Number.isFinite(raw.size) ? raw.size : 0,
	};
};

const toMessage = (value: unknown): ConsultChatMessage | null => {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;

	const raw = value as Record<string, unknown>;
	const id = typeof raw.id === "string" ? raw.id.trim() : "";
	if (!id || !SENDER_ROLES.includes(raw.senderRole as string) || !MESSAGE_TYPES.includes(raw.type as string)) return null;
	if (typeof raw.content !== "string" || typeof raw.timestamp !== "number" || !Number.isFinite(raw.timestamp)) return null;

	const senderName = typeof raw.senderName === "string" ? raw.senderName.trim() : "";
	const attachments = Array.isArray(raw.attachments) ? raw.attachments.map(toAttachment).filter((a): a is ConsultChatAttachment => a !== null) : [];

	return {
		id,
		senderRole: raw.senderRole as ConsultChatMessage["senderRole"],
		senderUserId: typeof raw.senderUserId === "string" && raw.senderUserId ? raw.senderUserId : null,
		senderName: senderName || null,
		type: raw.type as ConsultChatMessage["type"],
		content: raw.content,
		attachments,
		timestamp: raw.timestamp,
	};
};

/** Stored order preserved. An invalid message is dropped alone; any other failure yields `[]`. Never throws. */
export const parseChat = (value: unknown): ConsultChatMessage[] => {
	let candidate: unknown = value;

	// At most two unwraps — a JSON string, and a JSON string that decoded to another one, as `parseSpeakerMap` allows.
	for (let depth = 0; depth < 2 && typeof candidate === "string"; depth += 1) {
		const trimmed = candidate.trim();
		if (!trimmed) return [];

		try {
			candidate = JSON.parse(trimmed);
		} catch {
			return [];
		}
	}

	if (!Array.isArray(candidate)) return [];

	return candidate.map(toMessage).filter((m): m is ConsultChatMessage => m !== null);
};
